import React from "react";

import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { formatISO } from "date-fns";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { graphql } from "@/gql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { formatISODate } from "@/utils/datetime";
import { displayError } from "@/utils/errors";

import { clientWeightLogsPageQuery } from "../ClientWeightLogsPage";

type ModalProps = {
  visibility: boolean;
  onClose: () => void;
};
const useStyles = makeStyles()((theme) => ({
  root: {
    overflowY: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(24, 24),
    backgroundColor: colors.white,
    overflowY: "auto",
    width: "470px",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    margin: theme.spacing(4, 3, 0, 3),
  },
  modalHeaderText: {
    fontSize: "28px",
    fontWeight: "bold",
  },
  titleText: {
    fontSize: "14px",
    fontWeight: 700,
    color: colors.dark,
    marginBottom: "20px",
  },
  form: {
    margin: theme.spacing(4, 3, 0, 3),
  },
  fields: {
    width: "100%",
  },
  fieldContainer: {
    marginBottom: "20px",
  },
  error: {
    fontSize: theme.typography.pxToRem(12),
  },
}));

const FieldLabel = withStyles(Typography, {
  root: {
    color: colors.grey,
    fontSize: "14px",
    marginBottom: "12px",
    "& label": {
      color: "#D13F3F",
    },
  },
});

type FormValues = {
  weight: string;
  date: Date;
};

export const validationSchema = yup.object().shape({
  weight: yup
    .number()
    .required("Weight is required")
    .nullable()
    .transform((value) => (isNaN(value) ? null : value))
    .test(
      "has2decimal",
      "Value must not exceed 2 decimal places",
      function (value) {
        if (!value) return true;
        if (Number.isInteger(value)) return true;
        const result = value.toString().split(".")[1]
          ? value.toString().split(".")[1].length <= 2
          : true;
        return result;
      }
    ),
});

const createCurrentWeightMutation = graphql(`
  mutation CreateCurrentWeight($input: CreateWeightInput!) {
    createWeight(input: $input) {
      dailyWeight {
        id
      }
      errors {
        fullMessage
      }
    }
  }
`);

const AddWeightGoalModal: React.FC<ModalProps> = ({ visibility, onClose }) => {
  const { classes } = useStyles();
  const [createCurrentWeight] = useMutation(createCurrentWeightMutation);
  const { clientId, date } = useParams<{ clientId: string; date: string }>();
  const snackBar = useGlobalSnackbar();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      weight: "",
      date: new Date(date || new Date().toDateString()),
    },
    mode: "onChange",
    reValidateMode: "onBlur",
    resolver: yupResolver(validationSchema),
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const formValues = validationSchema.cast(values);
    try {
      const result = await createCurrentWeight({
        variables: {
          input: {
            clientId: clientId,
            date: formatISO(values.date, { representation: "date" }),
            weightKg: formValues?.weight ?? 0,
          },
        },
        refetchQueries: [
          {
            query: clientWeightLogsPageQuery,
            variables: {
              clientId: clientId ?? "",
              date: date ?? formatISODate(new Date()),
            },
          },
        ],
      });

      if (result.data) {
        snackBar.open("Current weight successfully added");
        onClose();
      }
    } catch (e) {
      snackBar.open(displayError(e, "Error: cannot add current weight"));
    }
  };

  return (
    <>
      <Modal open={visibility} onClose={onClose} className={classes.root}>
        <Box className={classes.modalBody}>
          <Box className={classes.modalHeader}>
            <Typography className={classes.modalHeaderText}>
              Add Weight
            </Typography>
            <IconButton onClick={onClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <form
            className={classes.form}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box>
              <Box className={classes.fieldContainer}>
                <FieldLabel>Weight</FieldLabel>
                <Controller
                  name="weight"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <>
                        <TextField
                          {...field}
                          className={classes.fields}
                          type="number"
                          placeholder="E.g. 75 kg"
                        />

                        <Typography
                          variant="subtitle2"
                          color="error"
                          className={classes.error}
                        >
                          {error?.message}
                        </Typography>
                      </>
                    );
                  }}
                />
              </Box>
              <Box className={classes.fieldContainer}>
                <FieldLabel>Date</FieldLabel>
                <Controller
                  control={control}
                  name="date"
                  render={({
                    field: { ref, name, onChange, ...field },
                    fieldState,
                  }) => (
                    <DatePicker
                      {...field}
                      inputRef={ref}
                      onChange={(value) => onChange(value as Date)}
                      disablePast
                      slotProps={{
                        textField: {
                          name,
                          fullWidth: true,
                          error: !!fieldState?.error,
                          helperText: fieldState?.error?.message,
                        },
                      }}
                    />
                  )}
                />
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                marginBottom={4}
              >
                <Button color="primary" type="submit" disabled={isSubmitting}>
                  Save
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default AddWeightGoalModal;
